import React, {
    createContext,
    createElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { BellRing, Gift, Megaphone, Sparkles, Ticket, Vote, X } from "lucide-react";

export const PROMO_NOTIFICATION_EVENT = "qit:promo-notification";

export type PromoNotificationVariant = "gift" | "votes" | "promo" | "feature";

export interface PromoNotificationPayload {
    id?: string;
    variant?: PromoNotificationVariant;
    eyebrow?: string;
    title: string;
    message: string;
    rewardLabel?: string;
    rewardAmount?: number;
    graphicalReward?: boolean;
    imageUrl?: string;
    actionLabel?: string;
    actionHref?: string;
    dismissLabel?: string;
    showOnce?: boolean;
}

interface PromoNotificationContextValue {
    showPromoNotification: (payload: PromoNotificationPayload) => void;
    dismissPromoNotification: () => void;
}

const DISMISSED_STORAGE_KEY = "qit:promo-notifications-dismissed";

const PromoNotificationContext = createContext<PromoNotificationContextValue | undefined>(undefined);

const variantConfig: Record<
    PromoNotificationVariant,
    {
        eyebrow: string;
        icon: React.ElementType;
        accentClass: string;
        panelClass: string;
        artworkClass: string;
    }
> = {
    gift: {
        eyebrow: "Gift drop",
        icon: Gift,
        accentClass: "text-fuchsia-500",
        panelClass: "border-fuchsia-500/30 bg-fuchsia-500/10",
        artworkClass: "bg-fuchsia-500/15 text-fuchsia-500",
    },
    votes: {
        eyebrow: "Free votes",
        icon: Vote,
        accentClass: "text-sky-500",
        panelClass: "border-sky-500/30 bg-sky-500/10",
        artworkClass: "bg-sky-500/15 text-sky-500",
    },
    promo: {
        eyebrow: "Promo",
        icon: Ticket,
        accentClass: "text-amber-500",
        panelClass: "border-amber-500/30 bg-amber-500/10",
        artworkClass: "bg-amber-500/15 text-amber-500",
    },
    feature: {
        eyebrow: "New on Qit",
        icon: Megaphone,
        accentClass: "text-emerald-500",
        panelClass: "border-emerald-500/30 bg-emerald-500/10",
        artworkClass: "bg-emerald-500/15 text-emerald-500",
    },
};

const readDismissedIds = (): string[] => {
    if (typeof window === "undefined") return [];

    try {
        const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
    } catch {
        return [];
    }
};

const writeDismissedId = (id: string) => {
    if (typeof window === "undefined") return;
    const next = Array.from(new Set([...readDismissedIds(), id])).slice(-100);
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
};

const shouldSkipNotification = (payload: PromoNotificationPayload): boolean => {
    if (!payload.showOnce || !payload.id) return false;
    return readDismissedIds().includes(payload.id);
};

const normalizePayload = (payload: PromoNotificationPayload): PromoNotificationPayload => ({
    variant: "gift",
    dismissLabel: "Maybe later",
    actionLabel: "View details",
    ...payload,
});

const PromoNotificationArtwork = ({
    payload,
}: {
    payload: PromoNotificationPayload;
}) => {
    const variant = payload.variant || "gift";
    const config = variantConfig[variant];
    const Icon = config.icon;
    const rewardAmount = Number(payload.rewardAmount);
    const imageUrl = String(payload.imageUrl || "").trim();
    const showGraphicalReward =
        payload.graphicalReward &&
        Number.isFinite(rewardAmount) &&
        rewardAmount > 0;

    return (
        <div className="relative mx-auto flex h-36 w-full max-w-[18rem] items-center justify-center overflow-hidden rounded-lg border border-border/70 bg-background/70">
            <div className="absolute left-5 top-5 h-12 w-7 rotate-[-18deg] rounded-md border border-primary/25 bg-primary/10" />
            <div className="absolute right-6 top-6 h-9 w-14 rotate-[14deg] rounded-md border border-amber-500/30 bg-amber-500/10" />
            <div className="absolute bottom-5 left-8 h-3 w-16 rotate-[8deg] rounded-full bg-sky-500/25" />
            <div className="absolute bottom-7 right-10 h-3 w-12 rotate-[-12deg] rounded-full bg-fuchsia-500/25" />
            <div className="absolute left-1/2 top-4 h-2 w-2 -translate-x-1/2 rounded-sm bg-emerald-500/60" />
            <div className="absolute left-12 top-20 h-2 w-2 rotate-45 rounded-sm bg-amber-500/70" />
            <div className="absolute right-14 top-24 h-2 w-2 rotate-45 rounded-sm bg-primary/70" />

            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={payload.rewardLabel || payload.title}
                    className="relative h-full w-full object-cover"
                    loading="eager"
                />
            ) : showGraphicalReward ? (
                <div className={`relative flex h-24 min-w-36 flex-col items-center justify-center rounded-lg border px-5 text-center shadow-xl ${config.artworkClass}`}>
                    <div className="flex items-center gap-2">
                        <Vote size={26} strokeWidth={1.8} />
                        <span className="text-3xl font-black leading-none">+{Math.floor(rewardAmount)}</span>
                    </div>
                    <span className="mt-2 text-xs font-semibold uppercase tracking-[0.16em]">votes granted</span>
                    <Sparkles className="absolute -right-3 -top-3 h-7 w-7 text-primary" />
                </div>
            ) : (
                <div
                    className={`relative flex h-24 w-24 items-center justify-center rounded-lg border shadow-xl ${config.artworkClass}`}
                >
                    <Icon size={46} strokeWidth={1.8} />
                    <Sparkles className="absolute -right-3 -top-3 h-7 w-7 text-primary" />
                </div>
            )}
        </div>
    );
};

const PromoNotificationLayer = ({
    payload,
    onDismiss,
}: {
    payload: PromoNotificationPayload;
    onDismiss: () => void;
}) => {
    const variant = payload.variant || "gift";
    const config = variantConfig[variant];

    const handleDismiss = useCallback(() => {
        if (payload.showOnce && payload.id) {
            writeDismissedId(payload.id);
        }
        onDismiss();
    }, [onDismiss, payload.id, payload.showOnce]);

    const handleAction = useCallback(() => {
        if (payload.showOnce && payload.id) {
            writeDismissedId(payload.id);
        }
        onDismiss();
        if (payload.actionHref && typeof window !== "undefined") {
            window.location.href = payload.actionHref;
        }
    }, [onDismiss, payload.actionHref, payload.id, payload.showOnce]);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/65 px-4 py-6 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-notification-title"
        >
            <div className="relative w-full max-w-[26rem] overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-2xl">
                <div className={`absolute inset-x-0 top-0 h-1 ${config.panelClass}`} />
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/80 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Dismiss notification"
                >
                    <X size={16} />
                </button>

                <div className="space-y-5 p-5 pt-7 sm:p-6 sm:pt-8">
                    <PromoNotificationArtwork payload={payload} />

                    <div className="space-y-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                            <BellRing size={14} className={config.accentClass} />
                            <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${config.accentClass}`}>
                                {payload.eyebrow || config.eyebrow}
                            </span>
                        </div>

                        <h2 id="promo-notification-title" className="text-2xl font-bold leading-tight text-foreground">
                            {payload.title}
                        </h2>
                        <p className="text-sm leading-6 text-muted-foreground">{payload.message}</p>

                        {payload.rewardLabel && (
                            <div className={`mx-auto rounded-lg border px-4 py-3 text-sm font-semibold ${config.panelClass}`}>
                                {payload.rewardLabel}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                        <button
                            type="button"
                            onClick={handleAction}
                            className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
                        >
                            {payload.actionLabel || "View details"}
                        </button>
                        <button
                            type="button"
                            onClick={handleDismiss}
                            className="rounded-lg border border-border bg-background px-4 py-3 text-sm font-semibold text-muted-foreground transition hover:text-foreground"
                        >
                            {payload.dismissLabel || "Maybe later"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export const PromoNotificationProvider = ({ children }: { children: React.ReactNode }) => {
    const [activePayload, setActivePayload] = useState<PromoNotificationPayload | null>(null);
    const [queue, setQueue] = useState<PromoNotificationPayload[]>([]);

    const showPromoNotification = useCallback((payload: PromoNotificationPayload) => {
        if (!payload?.title || !payload?.message || shouldSkipNotification(payload)) return;
        const normalized = normalizePayload(payload);
        setActivePayload((current) => {
            if (current) {
                setQueue((items) => [...items, normalized]);
                return current;
            }
            return normalized;
        });
    }, []);

    const dismissPromoNotification = useCallback(() => {
        setActivePayload(null);
        setQueue((items) => {
            const [next, ...rest] = items;
            if (next) {
                window.setTimeout(() => setActivePayload(next), 120);
            }
            return rest;
        });
    }, []);

    useEffect(() => {
        const handlePromoEvent = (event: Event) => {
            const customEvent = event as CustomEvent<PromoNotificationPayload>;
            showPromoNotification(customEvent.detail);
        };

        window.addEventListener(PROMO_NOTIFICATION_EVENT, handlePromoEvent as EventListener);
        return () => {
            window.removeEventListener(PROMO_NOTIFICATION_EVENT, handlePromoEvent as EventListener);
        };
    }, [showPromoNotification]);

    const value = useMemo(
        () => ({
            showPromoNotification,
            dismissPromoNotification,
        }),
        [dismissPromoNotification, showPromoNotification]
    );

    return (
        <PromoNotificationContext.Provider value={value}>
            {children}
            {activePayload &&
                createElement(PromoNotificationLayer, {
                    payload: activePayload,
                    onDismiss: dismissPromoNotification,
                })}
        </PromoNotificationContext.Provider>
    );
};

export const usePromoNotification = () => {
    const context = useContext(PromoNotificationContext);
    if (!context) {
        throw new Error("usePromoNotification must be used within PromoNotificationProvider");
    }
    return context;
};
